/**
 * SlackNotificationsPanel — multi-workspace UI.
 *
 * Modes via `scope` prop:
 *   - 'user'   → /api/integrations/slack/*           (N workspaces per user)
 *   - 'agency' → /api/integrations/agency-slack/*    (N workspaces per agency,
 *                with optional clientFilter whitelist that controls which
 *                client users' events get forwarded; null/empty = all clients)
 *
 * Layout: top section lists every connected workspace (refresh/rename/disconnect
 * + "Add another"). Below, one row per event-type with a workspace picker and a
 * channel picker (channels come from the picked workspace's cached list).
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  MessageSquare,
  Plug,
  Unplug,
  Send,
  RefreshCw,
  Info,
  Users,
  Pencil,
  Plus,
  SlidersHorizontal,
} from "lucide-react";
import {
  KPI_METRIC_CATALOG,
  resolveEffectiveKpiMetrics,
  type ClientKpiTargets,
  type KpiMetricId,
  type KpiSettings,
} from "@shared/kpi-settings";

const EVENT_TYPES = ["kpi_daily", "integration_failure", "campaign_milestone", "new_lead"] as const;
type EventType = (typeof EVENT_TYPES)[number];
type Destination = "channel" | "dm";
export type SlackPanelScope = "user" | "agency";

interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
}

interface Workspace {
  id: string;
  teamId: string;
  teamName: string | null;
  label: string | null;
  authedUserId: string | null;
  channels: SlackChannel[];
  channelsRefreshedAt: string | null;
}

interface WorkspacesResponse {
  integrations: Workspace[];
}

interface PreferenceRow {
  /** Unified front-end key — backend serializes as `slackIntegrationId` (user) or `agencySlackIntegrationId` (agency). */
  integrationId: string | null;
  eventType: EventType;
  enabled: boolean;
  destination: Destination;
  channelId: string | null;
  channelName: string | null;
  /** Warsaw local hour (0..23) for the kpi_daily digest. User scope only. */
  sendHour: number;
  /** Agency-only. null/[] = all clients in agency, otherwise whitelist of client userIds. */
  clientFilter?: string[] | null;
}

const DEFAULT_SEND_HOUR = 6;

interface PreferencesResponse {
  preferences: Array<
    Omit<PreferenceRow, "integrationId"> & {
      slackIntegrationId?: string | null;
      agencySlackIntegrationId?: string | null;
    }
  >;
}

export interface AgencyClientOption {
  userId: string;
  label: string;
}

interface ScopeConfig {
  basePath: string;
  workspacesKey: string;
  prefsKey: string;
  hubInvalidateKey: string;
  oauthMessagePrefix: "SLACK_OAUTH" | "AGENCY_SLACK_OAUTH";
  prefIntegrationFkField: "slackIntegrationId" | "agencySlackIntegrationId";
}

const SCOPE_CONFIG: Record<SlackPanelScope, ScopeConfig> = {
  user: {
    basePath: "/api/integrations/slack",
    workspacesKey: "slack-workspaces",
    prefsKey: "slack-preferences",
    hubInvalidateKey: "hub-slack",
    oauthMessagePrefix: "SLACK_OAUTH",
    prefIntegrationFkField: "slackIntegrationId",
  },
  agency: {
    basePath: "/api/integrations/agency-slack",
    workspacesKey: "agency-slack-workspaces",
    prefsKey: "agency-slack-preferences",
    hubInvalidateKey: "hub-agency-slack",
    oauthMessagePrefix: "AGENCY_SLACK_OAUTH",
    prefIntegrationFkField: "agencySlackIntegrationId",
  },
};

function openOAuthPopup(
  url: string,
  prefix: "SLACK_OAUTH" | "AGENCY_SLACK_OAUTH",
  onSuccess: () => void
): void {
  const width = 600;
  const height = 700;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
  const popup = window.open(
    url,
    "slack_oauth",
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
  );
  if (!popup) return;

  const handler = (event: MessageEvent) => {
    if (event.data?.type === `${prefix}_SUCCESS`) {
      window.removeEventListener("message", handler);
      onSuccess();
    } else if (event.data?.type === `${prefix}_ERROR`) {
      window.removeEventListener("message", handler);
    }
  };
  window.addEventListener("message", handler);
}

interface SlackNotificationsPanelProps {
  /** 'user' (default) or 'agency'. Determines which API + tables back the panel. */
  scope?: SlackPanelScope;
  /** Required for scope='agency': list of client users so agency owner can build clientFilter. */
  agencyClients?: AgencyClientOption[];
}

export function SlackNotificationsPanel({
  scope = "user",
  agencyClients = [],
}: SlackNotificationsPanelProps = {}): JSX.Element {
  const { t } = useTranslation("analyticsSettings");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [testingFor, setTestingFor] = useState<EventType | null>(null);
  const [testingKpiReport, setTestingKpiReport] = useState(false);

  const cfg = SCOPE_CONFIG[scope];

  const workspacesQuery = useQuery<WorkspacesResponse>({
    queryKey: [cfg.workspacesKey],
    queryFn: () => apiRequest("GET", cfg.basePath),
    staleTime: 30_000,
  });

  const workspaces = useMemo(
    () => workspacesQuery.data?.integrations ?? [],
    [workspacesQuery.data?.integrations]
  );
  const hasAny = workspaces.length > 0;

  const preferencesQuery = useQuery<PreferencesResponse>({
    queryKey: [cfg.prefsKey],
    queryFn: () => apiRequest("GET", `${cfg.basePath}/preferences`),
    enabled: hasAny,
    staleTime: 30_000,
  });

  // Normalize backend prefs into the panel's unified shape.
  const preferences = useMemo<PreferenceRow[]>(() => {
    const stored = preferencesQuery.data?.preferences ?? [];
    const byType = new Map<EventType, PreferenceRow>();
    for (const p of stored) {
      const integrationId =
        scope === "user" ? (p.slackIntegrationId ?? null) : (p.agencySlackIntegrationId ?? null);
      byType.set(p.eventType, {
        integrationId,
        eventType: p.eventType,
        enabled: p.enabled,
        destination: p.destination,
        channelId: p.channelId,
        channelName: p.channelName,
        sendHour: p.sendHour ?? DEFAULT_SEND_HOUR,
        clientFilter: p.clientFilter ?? null,
      });
    }
    return EVENT_TYPES.map((eventType) => {
      const existing = byType.get(eventType);
      if (existing) return existing;
      return {
        integrationId: hasAny ? workspaces[0].id : null,
        eventType,
        enabled: false,
        destination: "dm" as Destination,
        channelId: null,
        channelName: null,
        sendHour: DEFAULT_SEND_HOUR,
        clientFilter: null,
      };
    });
  }, [preferencesQuery.data, scope, hasAny, workspaces]);

  const updatePreference = useMutation({
    mutationFn: (row: PreferenceRow) => {
      if (!row.integrationId) {
        throw new Error("integrationId is required");
      }
      const body = {
        [cfg.prefIntegrationFkField]: row.integrationId,
        eventType: row.eventType,
        enabled: row.enabled,
        destination: row.destination,
        channelId: row.channelId,
        channelName: row.channelName,
        ...(scope === "agency"
          ? { clientFilter: row.clientFilter ?? null }
          : { sendHour: row.sendHour }),
      };
      return apiRequest("PUT", `${cfg.basePath}/preferences`, body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [cfg.prefsKey] }),
    onError: () => toast({ title: t("slack.errorTitle"), variant: "destructive" }),
  });

  const handleConnect = async () => {
    // Slack OAuth picks the workspace from the browser cookie by default — which traps
    // users who happen to be logged into a workspace they don't admin (Responso etc).
    // Asking for the workspace subdomain forces Slack to use that specific context
    // (https://<sub>.slack.com/oauth/...) and prompts re-login if needed.
    const raw = window.prompt(
      'Subdomena Slack workspace, do którego chcesz się podłączyć (np. "viralpartners" jeśli adres to viralpartners.slack.com).\n\nZostaw puste, żeby użyć tego workspace, do którego jesteś aktualnie zalogowany w przeglądarce.',
      ""
    );
    if (raw === null) return; // user clicked Cancel
    const subdomain = raw
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\.slack\.com.*$/, "");
    if (subdomain && !/^[a-z0-9][a-z0-9-]{0,62}$/.test(subdomain)) {
      toast({
        title: "Niepoprawna subdomena Slack",
        description: 'Tylko litery, cyfry i myślniki (np. "viralpartners")',
        variant: "destructive",
      });
      return;
    }

    setConnecting(true);
    try {
      const qs = subdomain ? `?workspace=${encodeURIComponent(subdomain)}` : "";
      const res = (await apiRequest("GET", `${cfg.basePath}/connect${qs}`)) as { url?: string };
      if (!res.url) throw new Error("missing_url");
      openOAuthPopup(res.url, cfg.oauthMessagePrefix, () => {
        queryClient.invalidateQueries({ queryKey: [cfg.workspacesKey] });
        queryClient.invalidateQueries({ queryKey: [cfg.hubInvalidateKey] });
        toast({ title: t("slack.connectedToastTitle") });
      });
    } catch {
      toast({ title: t("slack.connectErrorTitle"), variant: "destructive" });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnectWorkspace = async (workspaceId: string, name: string) => {
    if (!window.confirm(`${t("slack.confirmDisconnect")} (${name})`)) return;
    try {
      await apiRequest("DELETE", `${cfg.basePath}/${workspaceId}`);
      queryClient.invalidateQueries({ queryKey: [cfg.workspacesKey] });
      queryClient.invalidateQueries({ queryKey: [cfg.prefsKey] });
      queryClient.invalidateQueries({ queryKey: [cfg.hubInvalidateKey] });
      toast({ title: t("slack.disconnectedToastTitle") });
    } catch {
      toast({ title: t("slack.errorTitle"), variant: "destructive" });
    }
  };

  const handleRefreshChannels = async (workspaceId: string) => {
    setRefreshingId(workspaceId);
    try {
      await apiRequest("POST", `${cfg.basePath}/${workspaceId}/channels/refresh`);
      queryClient.invalidateQueries({ queryKey: [cfg.workspacesKey] });
      toast({ title: t("slack.channelsRefreshedToast") });
    } catch {
      toast({ title: t("slack.errorTitle"), variant: "destructive" });
    } finally {
      setRefreshingId(null);
    }
  };

  const handleRename = async (
    workspaceId: string,
    currentLabel: string | null,
    currentTeamName: string | null
  ) => {
    setRenamingId(workspaceId);
    try {
      const next = window.prompt(
        "Nazwa workspace (puste = użyj nazwy z Slacka):",
        currentLabel ?? currentTeamName ?? ""
      );
      if (next === null) return;
      const trimmed = next.trim();
      await apiRequest("POST", `${cfg.basePath}/${workspaceId}/rename`, {
        label: trimmed.length === 0 ? null : trimmed,
      });
      queryClient.invalidateQueries({ queryKey: [cfg.workspacesKey] });
    } catch {
      toast({ title: t("slack.errorTitle"), variant: "destructive" });
    } finally {
      setRenamingId(null);
    }
  };

  const handleTest = async (eventType: EventType) => {
    setTestingFor(eventType);
    try {
      await apiRequest("POST", `${cfg.basePath}/test-message`, { eventType });
      toast({ title: t("slack.testSentToastTitle") });
    } catch {
      toast({ title: t("slack.testErrorToastTitle"), variant: "destructive" });
    } finally {
      setTestingFor(null);
    }
  };

  // Force-send the real KPI digest on demand (user scope only). Does not touch
  // the once-per-day dedupe, so it never suppresses the scheduled send.
  const handleTestKpiReport = async () => {
    setTestingKpiReport(true);
    try {
      await apiRequest("POST", `${cfg.basePath}/kpi-daily/test`);
      toast({ title: t("slack.kpiTest.sentToast") });
    } catch (err) {
      const description = err instanceof Error ? err.message : undefined;
      toast({ title: t("slack.kpiTest.errorToast"), description, variant: "destructive" });
    } finally {
      setTestingKpiReport(false);
    }
  };

  const titleKey = scope === "agency" ? "slack.titleAgency" : "slack.title";
  const descKey = scope === "agency" ? "slack.descAgency" : "slack.descOauth";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-green-400" />
        <h3 className="text-sm font-semibold text-white">
          {scope === "agency"
            ? t(titleKey, "Slack agencji (powiadomienia ze wszystkich klientów)")
            : t(titleKey)}
        </h3>
      </div>
      <p className="text-xs text-white/60">
        {scope === "agency"
          ? t(
              descKey,
              "Podepnij osobny workspace agencji żeby dostawać kopię eventów ze wszystkich klientów."
            )
          : t(descKey)}
      </p>

      {!hasAny && (
        <div className="bg-blue-500/[0.06] border border-blue-500/20 rounded-lg px-3 py-2 flex gap-2">
          <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-white/70 leading-relaxed">
            <strong className="text-white/90">Wskazówka:</strong> Po kliknięciu "Połącz Slack"
            zapyta Cię o subdomenę workspace (np.{" "}
            <code className="text-white/90">viralpartners</code> z adresu viralpartners.slack.com).
            Wpisz tę, którą chcesz podłączyć — Slack przekieruje Cię na tę konkretną i poprosi o
            zalogowanie jeśli nie masz aktywnej sesji. Możesz dodać kilka workspace'ów osobno.
          </p>
        </div>
      )}

      {hasAny ? (
        <div className="space-y-1.5">
          {workspaces.map((w) => (
            <WorkspaceRow
              key={w.id}
              workspace={w}
              refreshing={refreshingId === w.id}
              renaming={renamingId === w.id}
              onRefresh={() => handleRefreshChannels(w.id)}
              onRename={() => handleRename(w.id, w.label, w.teamName)}
              onDisconnect={() => handleDisconnectWorkspace(w.id, w.label || w.teamName || w.id)}
            />
          ))}
          <Button
            size="sm"
            onClick={handleConnect}
            disabled={connecting}
            className="text-xs h-7 px-3 mt-1 bg-green-600 hover:bg-green-500"
          >
            {connecting ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Plus className="w-3 h-3 mr-1" />
            )}
            Dodaj kolejny workspace
          </Button>
        </div>
      ) : (
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-green-600 flex items-center justify-center">
              <MessageSquare className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <span className="text-sm text-white">{t("slack.notConnected")}</span>
              <p className="text-[10px] text-white/40">{t("slack.notConnectedDesc")}</p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleConnect}
            disabled={connecting}
            className="text-xs h-7 px-3 bg-green-600 hover:bg-green-500"
          >
            {connecting ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Plug className="w-3 h-3 mr-1" />
            )}
            {t("slack.connect")}
          </Button>
        </div>
      )}

      {hasAny && (
        <div className="space-y-2 pt-1">
          {preferences.map((pref) => (
            <EventPreferenceRow
              key={pref.eventType}
              pref={pref}
              workspaces={workspaces}
              scope={scope}
              agencyClients={agencyClients}
              onChange={(next) => updatePreference.mutate(next)}
              onTest={() => handleTest(pref.eventType)}
              testing={testingFor === pref.eventType}
              onTestKpiReport={handleTestKpiReport}
              testingKpiReport={testingKpiReport}
            />
          ))}
        </div>
      )}

      {hasAny && <KpiClientSettingsSection />}
    </div>
  );
}

/** Full agency client row as returned by GET /api/agency/clients (subset we use). */
interface AgencyKpiClient {
  id: string;
  name: string;
  businessType: string;
  /** Linked user account — null when the client has no dashboard user yet. */
  userId: string | null;
  /** Metric SELECTION + digest toggle (KPI VALUES no longer live here). */
  kpiSettings: KpiSettings | null;
  /** KPI target VALUES resolved from analyticsSettings (single source of truth). */
  effectiveKpi: ClientKpiTargets;
  /** Display name of the assigned agency member ("opiekun"), or null when unassigned. */
  ownerName?: string | null;
}

const KPI_TARGET_FIELDS = [
  "targetCpl",
  "targetCostPerCall",
  "targetCostPerPurchase",
  "targetRoas",
] as const;
type KpiTargetField = (typeof KPI_TARGET_FIELDS)[number];

/** Metric ids editable via checkboxes (spend is always shown, so excluded). */
const SELECTABLE_METRIC_IDS: KpiMetricId[] = (
  Object.keys(KPI_METRIC_CATALOG) as KpiMetricId[]
).filter((id) => id !== "spend");

/**
 * Per-client KPI digest config. Lists the agency's clients (agency-owner only
 * endpoint — silently hidden for users without access) and lets each client's
 * digest inclusion, KPI targets and metric set be edited. Writes to
 * agencyClients.kpiSettings via the dedicated PATCH endpoint.
 */
function KpiClientSettingsSection(): JSX.Element | null {
  const { t } = useTranslation("analyticsSettings");
  const clientsQuery = useQuery<{ clients: AgencyKpiClient[] }>({
    queryKey: ["/api/agency/clients", "kpi-settings"],
    queryFn: () => apiRequest("GET", "/api/agency/clients"),
    staleTime: 60_000,
    retry: false,
  });

  const clients = clientsQuery.data?.clients ?? [];

  // Hide entirely when the user cannot list clients (403) or has none.
  if (clientsQuery.isError || (!clientsQuery.isLoading && clients.length === 0)) return null;

  return (
    <div className="space-y-2 pt-2 border-t border-white/[0.08]">
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="w-3.5 h-3.5 text-green-400" />
        <h4 className="text-sm font-semibold text-white">{t("slack.kpiClients.title")}</h4>
      </div>
      <p className="text-xs text-white/60">{t("slack.kpiClients.desc")}</p>
      {clientsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-xs text-white/50">
          <Loader2 className="w-3 h-3 animate-spin" /> ...
        </div>
      ) : (
        <div className="space-y-1.5">
          {clients.map((client) => (
            <KpiClientRow key={client.id} client={client} />
          ))}
        </div>
      )}
    </div>
  );
}

interface KpiClientRowProps {
  client: AgencyKpiClient;
}

function KpiClientRow({ client }: KpiClientRowProps): JSX.Element {
  const { t } = useTranslation("analyticsSettings");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const settings = client.kpiSettings ?? {};
  // KPI target VALUES are pre-filled from the client's EFFECTIVE analyticsSettings
  // KPI (single source of truth) — NOT from agencyClients.kpiSettings.
  const initialTargets = client.effectiveKpi;
  const [digestEnabled, setDigestEnabled] = useState<boolean>(
    settings.slackDigestEnabled !== false
  );
  const [override, setOverride] = useState<KpiMetricId[]>(settings.slackMetricsOverride ?? []);
  const [targets, setTargets] = useState<Record<KpiTargetField, string>>({
    targetCpl: initialTargets.targetCpl != null ? String(initialTargets.targetCpl) : "",
    targetCostPerCall:
      initialTargets.targetCostPerCall != null ? String(initialTargets.targetCostPerCall) : "",
    targetCostPerPurchase:
      initialTargets.targetCostPerPurchase != null
        ? String(initialTargets.targetCostPerPurchase)
        : "",
    targetRoas: initialTargets.targetRoas != null ? String(initialTargets.targetRoas) : "",
  });

  const effectiveMetrics = resolveEffectiveKpiMetrics(
    client.businessType,
    override.length > 0 ? override : null
  );

  const save = useMutation({
    mutationFn: async () => {
      // 1) KPI target VALUES → analyticsSettings (single source of truth), keyed
      //    by the client's user. Empty input clears the target (sent as "").
      //    Skipped when the client has no linked user account.
      if (client.userId) {
        await apiRequest(
          "PATCH",
          `/api/analytics-settings?forClientUserId=${encodeURIComponent(client.userId)}`,
          {
            kpiCostPerLead: targets.targetCpl.trim(),
            kpiCostPerCall: targets.targetCostPerCall.trim(),
            kpiCostPerClient: targets.targetCostPerPurchase.trim(),
            kpiTargetRoas: targets.targetRoas.trim(),
          }
        );
      }
      // 2) Metric SELECTION + digest toggle → agencyClients.kpiSettings.
      await apiRequest("PATCH", `/api/integrations/slack-kpi-clients/${client.id}`, {
        slackMetricsOverride: override.length > 0 ? override : null,
        slackDigestEnabled: digestEnabled,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/clients", "kpi-settings"] });
      toast({ title: t("slack.kpiClients.saved") });
    },
    onError: () => toast({ title: t("slack.errorTitle"), variant: "destructive" }),
  });

  const toggleMetric = (id: KpiMetricId) => {
    setOverride((prev) => {
      // In default mode (empty override) seed from the resolved default set so
      // toggling one metric preserves the rest instead of collapsing to a
      // single-metric override.
      const base = prev.length > 0 ? prev : effectiveMetrics;
      return base.includes(id) ? base.filter((m) => m !== id) : [...base, id];
    });
  };

  const businessLabel = t(
    `slack.kpiClients.businessType.${client.businessType}`,
    client.businessType
  );

  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm text-white truncate">{client.name}</div>
          <div className="text-[10px] text-white/40">{businessLabel}</div>
          {client.ownerName && (
            <div className="text-[10px] text-white/40 truncate">
              {t("slack.kpiClients.owner", { name: client.ownerName })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[11px] text-white/60">{t("slack.kpiClients.includeInDigest")}</span>
          <Switch checked={digestEnabled} onCheckedChange={setDigestEnabled} />
        </div>
      </div>

      <div className="space-y-1">
        <span className="text-[11px] text-white/60">{t("slack.kpiClients.targetsLabel")}</span>
        <div className="grid grid-cols-2 gap-1.5">
          {KPI_TARGET_FIELDS.map((field) => (
            <label key={field} className="flex flex-col gap-0.5">
              <span className="text-[10px] text-white/50">
                {t(`slack.kpiClients.target.${field}`)}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={targets[field]}
                onChange={(e) => setTargets((prev) => ({ ...prev, [field]: e.target.value }))}
                className="h-7 bg-[#0a0c10] border border-white/[0.1] rounded px-2 text-xs text-white"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <span className="text-[11px] text-white/60">{t("slack.kpiClients.metricsLabel")}</span>
        <div className="flex flex-wrap gap-2">
          {SELECTABLE_METRIC_IDS.map((id) => {
            const checked =
              override.length > 0 ? override.includes(id) : effectiveMetrics.includes(id);
            return (
              <label
                key={id}
                className="flex items-center gap-1 text-[11px] text-white/80 cursor-pointer"
              >
                <input type="checkbox" checked={checked} onChange={() => toggleMetric(id)} />
                <span>{t(`slack.kpiClients.metric.${id}`)}</span>
              </label>
            );
          })}
        </div>
        <p className="text-[10px] text-white/40 italic">{t("slack.kpiClients.metricsHint")}</p>
      </div>

      <Button
        size="sm"
        onClick={() => save.mutate()}
        disabled={save.isPending}
        className="h-7 text-[11px] px-3 bg-green-600 hover:bg-green-500"
      >
        {save.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
        {t("slack.kpiClients.save")}
      </Button>
    </div>
  );
}

interface WorkspaceRowProps {
  workspace: Workspace;
  refreshing: boolean;
  renaming: boolean;
  onRefresh: () => void;
  onRename: () => void;
  onDisconnect: () => void;
}

function WorkspaceRow({
  workspace,
  refreshing,
  renaming,
  onRefresh,
  onRename,
  onDisconnect,
}: WorkspaceRowProps): JSX.Element {
  const display = workspace.label || workspace.teamName || workspace.teamId;
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-6 h-6 rounded bg-green-600 flex items-center justify-center flex-shrink-0">
          <MessageSquare className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-white truncate">{display}</div>
          <div className="text-[10px] text-white/40">
            {workspace.channels.length} kanałów
            {workspace.label ? ` · ${workspace.teamName ?? workspace.teamId}` : ""}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={refreshing}
          className="h-7 px-2 text-[11px] text-white/60 hover:text-white"
          title="Odśwież listę kanałów"
        >
          {refreshing ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRename}
          disabled={renaming}
          className="h-7 px-2 text-[11px] text-white/60 hover:text-white"
          title="Zmień nazwę"
        >
          <Pencil className="w-3 h-3" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDisconnect}
          className="h-7 px-2 text-[11px] border-red-500/40 text-red-400 hover:bg-red-500/10"
          title="Odłącz workspace"
        >
          <Unplug className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

interface EventPreferenceRowProps {
  pref: PreferenceRow;
  workspaces: Workspace[];
  scope: SlackPanelScope;
  agencyClients: AgencyClientOption[];
  onChange: (next: PreferenceRow) => void;
  onTest: () => void;
  testing: boolean;
  /** Force-send the real KPI digest now (user scope + kpi_daily only). */
  onTestKpiReport: () => void;
  testingKpiReport: boolean;
}

const SEND_HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);

function EventPreferenceRow({
  pref,
  workspaces,
  scope,
  agencyClients,
  onChange,
  onTest,
  testing,
  onTestKpiReport,
  testingKpiReport,
}: EventPreferenceRowProps): JSX.Element {
  const { t } = useTranslation("analyticsSettings");
  const eventLabel = t(`slack.event.${pref.eventType}.label`);
  const eventDesc = t(`slack.event.${pref.eventType}.desc`);
  const showKpiControls = scope === "user" && pref.eventType === "kpi_daily";

  const selectedWorkspace = workspaces.find((w) => w.id === pref.integrationId) ?? null;
  const channels = selectedWorkspace?.channels ?? [];

  const [localChannelId, setLocalChannelId] = useState(pref.channelId ?? "");
  useEffect(() => {
    setLocalChannelId(pref.channelId ?? "");
  }, [pref.channelId]);

  // Lokalny wybor celu. Przelaczenie na 'channel' tylko odslania dropdown — NIE zapisujemy
  // dopoki user nie wskaze kanalu, bo backend wymaga channelId dla destination=channel
  // (inaczej 400 "Błąd"). DM zapisuje sie od razu (jest poprawny bez channelId).
  const [localDestination, setLocalDestination] = useState<Destination>(pref.destination);
  useEffect(() => {
    setLocalDestination(pref.destination);
  }, [pref.destination]);

  const dirty = localChannelId !== (pref.channelId ?? "") && localDestination === "channel";

  const filterMode: "all" | "whitelist" =
    pref.clientFilter && pref.clientFilter.length > 0 ? "whitelist" : "all";

  const toggleClientInFilter = (userId: string) => {
    const current = pref.clientFilter ?? [];
    const next = current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId];
    onChange({ ...pref, clientFilter: next.length === 0 ? null : next });
  };

  const handleWorkspaceChange = (nextId: string) => {
    // Switching workspaces invalidates the channelId (different channel namespace).
    onChange({ ...pref, integrationId: nextId, channelId: null, channelName: null });
  };

  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <span className="text-sm text-white">{eventLabel}</span>
          <p className="text-[10px] text-white/40">{eventDesc}</p>
        </div>
        <Switch
          checked={pref.enabled}
          onCheckedChange={(enabled) => onChange({ ...pref, enabled })}
          disabled={!pref.integrationId}
        />
      </div>

      {pref.enabled && (
        <div className="space-y-2">
          {workspaces.length > 1 && (
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-white/60 flex-shrink-0">Workspace:</label>
              <select
                value={pref.integrationId ?? ""}
                onChange={(e) => handleWorkspaceChange(e.target.value)}
                className="flex-1 h-8 bg-[#0a0c10] border border-white/[0.1] rounded px-2 text-xs text-white"
              >
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.label || w.teamName || w.teamId}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2 text-[11px]">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name={`dest-${scope}-${pref.eventType}`}
                checked={localDestination === "dm"}
                onChange={() => {
                  setLocalDestination("dm");
                  onChange({ ...pref, destination: "dm", channelId: null, channelName: null });
                }}
              />
              <span className="text-white/80">{t("slack.dest.dm")}</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name={`dest-${scope}-${pref.eventType}`}
                checked={localDestination === "channel"}
                onChange={() => setLocalDestination("channel")}
              />
              <span className="text-white/80">{t("slack.dest.channel")}</span>
            </label>
          </div>

          {localDestination === "channel" && (
            <div className="flex items-center gap-2">
              <select
                value={localChannelId}
                onChange={(e) => setLocalChannelId(e.target.value)}
                className="flex-1 h-8 bg-[#0a0c10] border border-white/[0.1] rounded px-2 text-xs text-white"
              >
                <option value="">{t("slack.pickChannel")}</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.isPrivate ? "🔒 " : "#"}
                    {c.name}
                  </option>
                ))}
              </select>
              {dirty && (
                <Button
                  size="sm"
                  className="h-7 text-[11px] px-2"
                  onClick={() => {
                    const channel = channels.find((c) => c.id === localChannelId);
                    onChange({
                      ...pref,
                      destination: "channel",
                      channelId: localChannelId || null,
                      channelName: channel?.name ?? null,
                    });
                  }}
                  disabled={!localChannelId}
                >
                  {t("slack.save")}
                </Button>
              )}
            </div>
          )}

          {scope === "agency" && (
            <div className="border-t border-white/[0.06] pt-2 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Users className="w-3 h-3 text-white/50" />
                <span className="text-[11px] text-white/70 font-medium">Filtr klientów</span>
              </div>
              <div className="flex gap-2 text-[11px]">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name={`filter-${pref.eventType}`}
                    checked={filterMode === "all"}
                    onChange={() => onChange({ ...pref, clientFilter: null })}
                  />
                  <span className="text-white/80">Wszyscy klienci</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name={`filter-${pref.eventType}`}
                    checked={filterMode === "whitelist"}
                    onChange={() => onChange({ ...pref, clientFilter: pref.clientFilter ?? [] })}
                  />
                  <span className="text-white/80">Wybrani klienci</span>
                </label>
              </div>
              {filterMode === "whitelist" && (
                <div className="max-h-32 overflow-y-auto bg-black/20 border border-white/[0.06] rounded p-2 space-y-0.5">
                  {agencyClients.length === 0 && (
                    <p className="text-[10px] text-white/40 italic">Brak klientów do wyboru.</p>
                  )}
                  {agencyClients.map((c) => (
                    <label
                      key={c.userId}
                      className="flex items-center gap-2 cursor-pointer text-[11px] text-white/80 hover:text-white"
                    >
                      <input
                        type="checkbox"
                        checked={(pref.clientFilter ?? []).includes(c.userId)}
                        onChange={() => toggleClientInFilter(c.userId)}
                      />
                      <span className="truncate">{c.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {showKpiControls && (
            <div className="border-t border-white/[0.06] pt-2 space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-white/60 flex-shrink-0">
                  {t("slack.kpiTest.hourLabel")}
                </label>
                <select
                  value={pref.sendHour}
                  onChange={(e) => onChange({ ...pref, sendHour: Number(e.target.value) })}
                  className="h-8 bg-[#0a0c10] border border-white/[0.1] rounded px-2 text-xs text-white"
                >
                  {SEND_HOUR_OPTIONS.map((h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onTestKpiReport}
                disabled={testingKpiReport}
                className="h-7 text-[11px] px-2 border-green-600/40 text-green-400 hover:bg-green-600/10"
              >
                {testingKpiReport ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Send className="w-3 h-3 mr-1" />
                )}
                {t("slack.kpiTest.button")}
              </Button>
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={onTest}
            disabled={testing || (pref.destination === "channel" && !pref.channelId)}
            className="h-7 text-[11px] px-2 border-green-600/40 text-green-400 hover:bg-green-600/10"
          >
            {testing ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Send className="w-3 h-3 mr-1" />
            )}
            {t("slack.sendTest")}
          </Button>
        </div>
      )}
    </div>
  );
}
